import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';

import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useToast from '~/hooks/useToast';

interface Props {
  postId: string;
  commentId?: string;
  subscriptionId?: string;
  onReport: () => void;
}

const ReportAction = ({ postId, commentId, subscriptionId, onReport }: Props) => {
  const { t } = useTranslation();
  const fetcher = useFetcher();
  const isSubmitting = fetcher.state === 'submitting';
  const fetcherData = fetcher.data;

  useToast(fetcherData?.toast);

  useEffect(() => {
    if (fetcherData?.toast) {
      onReport();
    }
  }, [fetcherData?.toast, onReport]);

  const ctaText = t('comments.reporting.report');
  const ctaSubmittingText = `${t('comments.reporting.reporting')}...`;

  return (
    <div className="flex items-start space-x-4">
      <div className="min-w-0 flex-1">
        <fetcher.Form method="post" action="/comment_report" className="relative">
          <div className="overflow-hidden rounded-wt border border-gray-300 shadow-sm focus-within:border-wt-primary focus-within:ring-1 focus-within:ring-wt-primary">
            <label htmlFor="comment" className="sr-only">
              {t('comments.reporting.file_report')}
            </label>
            <textarea
              rows={2}
              name="reason"
              id="reason"
              required
              className="block w-full resize-none border-0 bg-transparent py-3 text-sm focus:ring-0"
              placeholder={t('comments.reporting.report_action_placeholder') || ''}
              defaultValue={''}
            />
            <input type="hidden" name="post_id" value={postId} />
            <input type="hidden" name="comment_id" value={commentId} />
            <input type="hidden" name="subscription_id" value={subscriptionId} />
            <StatusInputs
              name="comment_report"
              errorMessage={t('errors.default')}
              successMessage={t('comments.reporting.reported')}
            />

            <div className="py-2" aria-hidden="true">
              <div className="py-px">
                <div className="h-9" />
              </div>
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 flex justify-between py-2 pl-3 pr-2">
            <div className="flex items-center space-x-5" />
            <div className="flex-shrink-0 pr-1">
              <Button type="submit" btnSize="sm" disabled={isSubmitting}>
                {isSubmitting ? ctaSubmittingText : ctaText}
              </Button>
            </div>
          </div>
        </fetcher.Form>
      </div>
    </div>
  );
};

export default ReportAction;
